<template>
  <div class="flex flex-col flex-wrap border-solid border-t
         border-gray-300 bg-gray-100">
    <div class="sm:hidden flex flex-row justify-stretch items-stretch px-2 py-3">
      <button
        class="rounded bg-white text-gray-500 border-gray-300"
        :class="buttonClasses"
        aria-label="Previous Page"
        title="Previous Page"
        :disabled="currentPage === 1"
        @click="$emit('loadPage', currentPage - 1)"
      >
        Previous
      </button>
      <div class="w-full"></div>
      <button
        class="rounded bg-white text-gray-500 border-gray-300"
        :class="buttonClasses"
        aria-label="Next Page"
        title="Next Page"
        :disabled="currentPage === totalPages"
        @click="$emit('loadPage', currentPage + 1)"
      >
        Next
      </button>
    </div>

    <div class="hidden sm:flex flex-row px-2 py-3">
      <div class="flex w-full md:w-1/2 flex-grow items-center justify-start">
        <p class="text-base leading-5 text-gray-700">
          Showing
          <strong>{{ currentItemIndexes.start }}</strong>
          to
          <strong>{{ Math.min(currentItemIndexes.end, totalItems) }}</strong>
          of
          <strong>{{ totalItems }}</strong>
          results.
        </p>
      </div>
      <transition name="fade-fast">
        <div
          v-if="totalPages > 1"
          class="inline-flex w-full md:w-1/2 items-center md:justify-end justify-center"
        >
          <button
            class="rounded-l-md bg-white text-gray-500 border-gray-300"
            :class="[buttonClasses, {'opacity-50': currentPage === 1}]"
            aria-label="Previous Page"
            :disabled="currentPage === 1"
            @click="$emit('loadPage', currentPage - 1)"
          >
            <svg height="20" viewBox="0 0 24 24" width="20" fill="currentColor">
              <path d="M0 0h24v24H0V0z" fill="none"/>
              <path d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"/>
            </svg>
          </button>

          <button
            v-if="currentPage > 2"
            class="bg-white text-gray-500 border-gray-300"
            :class="buttonClasses"
            aria-label="Page 1"
            name="Page 1"
            @click="$emit('loadPage', 1)"
          >
            1
          </button>
          <button
            v-if="currentPage >= 3"
            class="bg-white text-gray-500 border-gray-300"
            :class="buttonClasses"
            disabled
          >
            ...
          </button>

          <button
            v-for="i in activePages"
            :key="`page-${i}`"
            :class="[
            buttonClasses,
            currentPage === i ? 'bg-green-500 text-white border-green-500'
            : 'bg-white text-gray-500 border-gray-300',
            ]"
            :aria-label="`Page ${i}`"
            :title="`Page ${i}`"
            @click="$emit('loadPage', i)"
          >
            {{ i }}
          </button>

          <button
            v-if="currentPage < totalPages - 2"
            class="bg-white text-gray-500 border-gray-300"
            :class="buttonClasses"
            disabled
          >
            ...
          </button>
          <button
            v-if="currentPage < totalPages - 1"
            class="bg-white text-gray-500 border-gray-300"
            :class="buttonClasses"
            aria-label="Page 10"
            name="Page 10"
            @click="$emit('loadPage', totalPages)"
          >
            {{ totalPages }}
          </button>

          <button
            class="rounded-r-md bg-white text-gray-500 border-gray-300"
            :class="[buttonClasses, {'opacity-50': currentPage === totalPages}]"
            aria-label="Next Page"
            title="Next Page"
            :disabled="currentPage === totalPages"
            @click="$emit('loadPage', currentPage + 1)"
          >
            <svg height="20" viewBox="0 0 24 24" width="20" fill="currentColor">
              <path d="M0 0h24v24H0V0z" fill="none"/>
              <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"/>
            </svg>
          </button>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Pagination',
  props: {
    currentPage: {
      type: Number,
      default: () => 1,
    },
    pageSize: {
      type: Number,
      default: () => 1,
    },
    totalItems: {
      type: Number,
      default: () => 0,
    },
    totalPages: {
      type: Number,
      default: () => 0,
    },
  },
  computed: {
    activePages() {
      const pages = [...Array(this.totalPages).keys()].map((i) => i + 1);
      return pages.filter((page) => page < (this.currentPage + 2) && page > (this.currentPage - 2));
    },
    currentItemIndexes() {
      return {
        start: ((this.currentPage - 1) * this.pageSize) + 1,
        end: this.currentPage * this.pageSize,
      };
    },
    buttonClasses() {
      return [
        'pagination-button', 'focus:outline-none', 'focus:shadow-outline-green',
      ];
    },
  },
};
</script>

<style lang="scss">
  .pagination-button {
    @apply relative inline-flex px-4 py-2 items-center justify-center text-center border
    text-sm leading-5 font-medium transition ease-in-out duration-150;
  }
</style>
